import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { useCurrency } from '../../contexts/CompanyContext';
import { ChartCard, TrendBarChart, DonutChart } from './FinanceCharts';
import { DrilldownDialog } from './DrilldownDialog';
import { DataNote } from './DataNote';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPES = [
  { key: 'asset', label: 'Assets', color: '#2563eb' },
  { key: 'liability', label: 'Liabilities', color: '#dc2626' },
  { key: 'equity', label: 'Equity', color: '#7c3aed' },
  { key: 'income', label: 'Income', color: '#059669' },
  { key: 'expense', label: 'Expenses', color: '#d97706' },
];

// Charts + account-type overview for the Chart of Accounts page
export function AccountsInsights({ accounts, overviewOpen, onOverviewClose }) {
  const { formatCurrency } = useCurrency();
  const [trend, setTrend] = useState([]);
  const [drill, setDrill] = useState(null);
  const year = new Date().getFullYear();
  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    axios.get(`${API_URL}/reports/drilldown?category=pnl&start_date=${year}-01-01&end_date=${today}`)
      .then((r) => setTrend((r.data.monthly || []).map((m) => ({ ...m, income: m.income || 0, expense: m.expense || 0 }))))
      .catch(() => setTrend([]));
  }, [year, today]);

  const byType = useMemo(() => TYPES.map((t) => {
    const list = accounts.filter((a) => a.account_type === t.key);
    return { ...t, count: list.length, balance: list.reduce((s, a) => s + (Number(a.balance) || 0), 0) };
  }), [accounts]);

  const openType = (key, label) => setDrill({ category: key, title: `${label} — all posted entries`, endDate: today });

  return (
    <>
      <DataNote />
      <div className="grid lg:grid-cols-3 gap-4" data-testid="accounts-insights">
        <ChartCard title={`Income vs Expenses — ${year}`} subtitle="Monthly, from posted journal entries" className="lg:col-span-2" testid="accounts-trend-chart">
          <TrendBarChart data={trend} series={[{ key: 'income', label: 'Income', color: '#059669' }, { key: 'expense', label: 'Expenses', color: '#dc2626' }]} />
        </ChartCard>
        <ChartCard title="Balances by account type" subtitle="Click a slice to see its entries" testid="accounts-type-chart">
          <DonutChart
            data={byType.map((t) => ({ name: t.label, value: t.balance, color: t.color, key: t.key }))}
            onSliceClick={(d) => d?.key && openType(d.key, d.name)}
          />
        </ChartCard>
      </div>

      <Dialog open={overviewOpen} onOpenChange={(o) => !o && onOverviewClose()}>
        <DialogContent className="max-w-2xl" data-testid="accounts-overview-dialog">
          <DialogHeader>
            <DialogTitle>Chart of Accounts overview</DialogTitle>
            <DialogDescription>{accounts.length} accounts · click a type to see every entry behind it</DialogDescription>
          </DialogHeader>
          <DonutChart data={byType.map((t) => ({ name: `${t.label} (${t.count})`, value: t.balance, color: t.color }))} height={200} />
          <div className="rounded-lg border divide-y">
            {byType.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => { onOverviewClose(); openType(t.key, t.label); }}
                className="w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-muted/40 transition-colors"
                data-testid={`accounts-overview-type-${t.key}`}
              >
                <span className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ background: t.color }} />
                  {t.label} <span className="text-muted-foreground text-xs">{t.count} accounts</span>
                </span>
                <span className="font-mono font-semibold">{formatCurrency(t.balance)}</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <DrilldownDialog request={drill} onClose={() => setDrill(null)} />
    </>
  );
}
